import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Plus, CheckCircle2, Eye, RotateCcw, X, Lock, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, Input } from "./Suppliers";
import { useAuth } from "../context/AuthContext";
import DeleteRequestButton from "../components/DeleteRequestButton";
import { usePager } from "../components/Pagination";
import useSubmitGuard from "../lib/useSubmitGuard";

const _rowId = () => `r-${Math.random().toString(36).slice(2, 9)}`;
const EMPTY = () => ({
  supplier_id: "",
  items: [{ _rowId: _rowId(), material_id: "", qty: 0, rate: 0, open_qty: false, hsn_code: "", gst_slab: 18 }],
  notes: "", delivery_terms: "", payment_terms: "", expected_delivery: "",
});
const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PurchaseOrders() {
  // Feb-2026 — submit guard against double-tap duplicates.
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [form, setForm] = useState(EMPTY());
  const [editingId, setEditingId] = useState(null);
  const [abnormal, setAbnormal] = useState([]);
  const [actionPo, setActionPo] = useState(null);  // {po, action: 'send-back' | 'pre-close'}
  const [actionRemark, setActionRemark] = useState("");
  const [q, setQ] = useState("");
  const { user } = useAuth();

  const load = async () => {
    const [p, s, m, ab] = await Promise.all([api.get("/purchase-orders"), api.get("/suppliers"), api.get("/materials"), api.get("/purchase-orders/abnormal-report")]);
    setPos(p.data); setSuppliers(s.data); setMaterials(m.data); setAbnormal(ab.data);
  };
  useEffect(() => { load(); }, []);

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
  const matName = (id) => materials.find((m) => m.id === id)?.name || "—";
  const matCode = (id) => materials.find((m) => m.id === id)?.code || "";
  const matHsn = (id) => materials.find((m) => m.id === id)?.hsn_code || "";
  const supObj = (id) => suppliers.find((s) => s.id === id) || {};
  const filteredMaterials = form.supplier_id ? materials.filter((m) => (m.supplier_ids || []).includes(form.supplier_id)) : [];

  const updateItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  // Form items get a synthetic `_rowId` so React can track per-row state
  // correctly across add/remove (using array index breaks state on remove —
  // the next row inherits the removed row's component state).
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { _rowId: _rowId(), material_id: "", qty: 0, rate: 0, open_qty: false, hsn_code: "", gst_slab: 18 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  // Feb-2026 (Wave E) — auto-fill HSN from material master when the row's
  // material is chosen and the operator hasn't overridden HSN yet.
  const onSelectMaterial = (i, matId) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) => idx === i
        ? { ...it, material_id: matId, hsn_code: it.hsn_code || (materials.find((m) => m.id === matId)?.hsn_code || "") }
        : it),
    }));
  };

  const save = async () => {
    if (!form.supplier_id || form.items.some((i) => !i.material_id || (!i.open_qty && !i.qty))) { toast.error("Supplier required, and items need either qty > 0 OR open_qty checked"); return; }
    try {
      const payload = {
        ...form,
        items: form.items.map((i) => ({
          material_id: i.material_id, rate: +i.rate || 0,
          qty: i.open_qty ? 0 : (+i.qty || 0),
          open_qty: !!i.open_qty,
          hsn_code: (i.hsn_code || "").trim(),
          gst_slab: +i.gst_slab || 0,
        })),
      };
      await submitGuard(async (idemKey) => {
        if (editingId) await api.post(`/purchase-orders/${editingId}/resubmit`, payload, { headers: { "Idempotency-Key": idemKey } });
        else await api.post("/purchase-orders", payload, { headers: { "Idempotency-Key": idemKey } });
      });
      setOpen(false); setEditingId(null);
      toast.success(editingId ? "Resubmitted" : "PO created — pending Super Admin approval");
      setForm(EMPTY()); resetGuard();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const approve = async (po) => {
    try { await api.post(`/purchase-orders/${po.id}/approve`); toast.success(`${po.number} approved`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitAction = async () => {
    if (!actionRemark.trim()) { toast.error("Remark required"); return; }
    let url;
    let successMsg;
    if (actionPo.action === "send-back") {
      url = `/purchase-orders/${actionPo.po.id}/send-back`;
      successMsg = "PO sent back to creator";
    } else if (actionPo.action === "pre-close") {
      url = `/purchase-orders/${actionPo.po.id}/pre-close`;
      successMsg = "Pre-close raised";
    } else if (actionPo.action === "reopen") {
      url = `/purchase-orders/${actionPo.po.id}/reopen-request`;
      successMsg = "Reopen request raised";
    } else if (actionPo.action === "reopen-reject") {
      url = `/purchase-orders/${actionPo.po.id}/reopen-reject`;
      successMsg = "Reopen rejected";
    }
    try {
      await api.post(url, { remark: actionRemark });
      toast.success(successMsg);
      setActionPo(null); setActionRemark(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const decidePO = async (decision) => {
    if ((decision === "send_back" || decision === "reject") && !actionRemark.trim()) {
      toast.error("Remark required for Send Back / Reject"); return;
    }
    try {
      if (decision === "approve") await api.post(`/purchase-orders/${actionPo.po.id}/approve`);
      else if (decision === "send_back") await api.post(`/purchase-orders/${actionPo.po.id}/send-back`, { remark: actionRemark });
      else if (decision === "reject") await api.post(`/purchase-orders/${actionPo.po.id}/reject`, { remark: actionRemark });
      toast.success(decision === "approve" ? "Approved" : decision === "send_back" ? "Sent back" : "Rejected");
      setActionPo(null); setActionRemark(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const editSentBack = (po) => {
    setForm({
      supplier_id: po.supplier_id,
      items: (po.items || []).map((it) => ({
        _rowId: _rowId(), material_id: it.material_id, qty: it.qty, rate: it.rate,
        open_qty: !!it.open_qty, hsn_code: it.hsn_code || "", gst_slab: it.gst_slab ?? 18,
      })),
      notes: po.notes || "",
      delivery_terms: po.delivery_terms || "",
      payment_terms: po.payment_terms || "",
      expected_delivery: po.expected_delivery || "",
    });
    setEditingId(po.id); setOpen(true);
  };

  const filtered = pos.filter((p) => {
    if (!q) return true;
    const k = q.toLowerCase();
    return (p.number || "").toLowerCase().includes(k) || supName(p.supplier_id).toLowerCase().includes(k);
  });
  // Wave-J pagination
  const { visibleRows: pagedRows, controls: pageControls } = usePager(filtered, 10);

  return (
    <div data-testid="purchase-orders-page">
      <PageHeader title="Purchase Orders" subtitle="Admin creates · SA approves · GRN auto-validates against rate & qty"
        helpText="POs are linked to Supplier + Materials. Status: pending → open → partial → closed. GRN creation flags rate/qty mismatches automatically. Abnormal weekly/monthly variance shown below."
        right={<button onClick={() => setOpen(true)} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-po-btn"><Plus size={14}/> New PO</button>}
      />
      <div className="p-8 space-y-6">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D2B5E]/40"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="po-search" placeholder="Search PO number or supplier…" className="w-full pl-9 pr-3 py-2 border border-[#EEEEEE] rounded-sm text-sm bg-white"/>
        </div>
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: "12%" }}/><col style={{ width: "20%" }}/><col style={{ width: "8%" }}/>
              <col style={{ width: "11%" }}/><col style={{ width: "16%" }}/><col style={{ width: "13%" }}/>
              <col style={{ width: "20%" }}/>
            </colgroup>
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">PO No.</th>
                <th className="text-left px-4 py-2">Supplier</th>
                <th className="text-right px-4 py-2"># Items</th>
                <th className="text-right px-4 py-2">Total Qty</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((p, i) => {
                const totalQty = (p.items || []).reduce((a, x) => a + (x.open_qty ? 0 : (x.qty || 0)), 0);
                const hasOpen = (p.items || []).some((x) => x.open_qty);
                return (
                  <tr key={p.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                    <td className="px-4 py-2 font-mono text-[#1565C0] truncate">{p.number}</td>
                    <td className="px-4 py-2 truncate">{supName(p.supplier_id)}</td>
                    <td className="px-4 py-2 font-mono text-right">{(p.items || []).length}</td>
                    <td className="px-4 py-2 font-mono text-right">{hasOpen ? <span className="text-[#E65100] font-bold">OPEN</span> : totalQty}</td>
                    <td className="px-4 py-2 text-xs truncate">{p.created_by_name}</td>
                    <td className="px-4 py-2"><StatusBadge status={p.status === "pending_approval" ? "pending_checker" : p.status === "open" ? "approved" : p.status}/></td>
                    <td className="px-4 py-2 text-right space-x-1">
                      <button onClick={() => setView(p)} className="text-[#1565C0]" data-testid={`view-po-${p.number}`}><Eye size={14}/></button>
                      {p.status === "pending_approval" && user?.role?.is_super_admin && (
                        <button onClick={() => { setActionPo({ po: p, action: "review" }); setActionRemark(""); }} className="text-xs bg-[#2E7D32] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`review-po-${p.number}`}><CheckCircle2 size={12}/>Review</button>
                      )}
                      {p.status === "sent_back" && (p.created_by === user?.id || user?.role?.is_super_admin) && (
                        <button onClick={() => editSentBack(p)} className="text-xs bg-[#1565C0] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`resubmit-po-${p.number}`}>Edit & Resubmit</button>
                      )}
                      {!["pending_approval", "sent_back", "closed", "pre_close_pending"].includes(p.status) && (user?.role?.is_super_admin || user?.role?.name === "Plant Head/Admin") && (
                        <button onClick={() => { setActionPo({ po: p, action: "pre-close" }); setActionRemark(""); }} className="text-xs bg-[#0D2B5E] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`preclose-po-${p.number}`}><Lock size={12}/>Pre-Close</button>
                      )}
                      {p.status === "pre_close_pending" && user?.role?.is_super_admin && (
                        <button onClick={async () => { try { await api.post(`/purchase-orders/${p.id}/pre-close/approve`); toast.success("Closed"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }} className="text-xs bg-[#C62828] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`approve-preclose-po-${p.number}`}>Approve Close</button>
                      )}
                      {/* User #2 — PO Reopen */}
                      {p.status === "closed" && (
                        <button onClick={() => { setActionPo({ po: p, action: "reopen" }); setActionRemark(""); }}
                          className="text-xs bg-[#2E7D32] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1"
                          data-testid={`reopen-po-${p.number}`}>Reopen</button>
                      )}
                      {p.status === "reopen_pending" && (user?.role?.is_super_admin || user?.role?.name === "Plant Head/Admin") && (
                        <>
                          <button onClick={async () => { try { await api.post(`/purchase-orders/${p.id}/reopen-approve`); toast.success("PO reopened"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }}
                            className="text-xs bg-[#2E7D32] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`approve-reopen-po-${p.number}`}>Approve Reopen</button>
                          <button onClick={() => { setActionPo({ po: p, action: "reopen-reject" }); setActionRemark(""); }}
                            className="text-xs bg-[#C62828] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`reject-reopen-po-${p.number}`}>Reject Reopen</button>
                        </>
                      )}
                      <span className="inline-block align-middle"><DeleteRequestButton module="purchase_order" record={p} label={p.number} onDeleted={load}/></span>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-[#0D2B5E]/50">No POs.</td></tr>}
            </tbody>
          </table>
          {pageControls}
        </div>

        <div className="bg-white border border-[#EEEEEE] rounded-sm">
          <div className="px-5 py-3 border-b border-[#EEEEEE] bg-[#F5F5F5] text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60">
            Abnormal Reports — Short / Excess vs PO (last 7 days)
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">PO</th>
                <th className="text-left px-4 py-2">Material</th>
                <th className="text-right px-4 py-2">PO Qty</th>
                <th className="text-right px-4 py-2">Received</th>
                <th className="text-right px-4 py-2">Variance</th>
                <th className="text-left px-4 py-2">Kind</th>
              </tr>
            </thead>
            <tbody>
              {abnormal.map((a, i) => (
                <tr key={`${a.po_number}-${a.material_id}-${i}`} className={i % 2 === 0 ? "" : "bg-[#F5F5F5]/40"}>
                  <td className="px-4 py-2 font-mono text-[#1565C0]">{a.po_number}</td>
                  <td className="px-4 py-2">{matCode(a.material_id)} — {matName(a.material_id)}</td>
                  <td className="px-4 py-2 font-mono text-right">{a.po_qty}</td>
                  <td className="px-4 py-2 font-mono text-right">{a.received_qty}</td>
                  <td className={`px-4 py-2 font-mono text-right ${a.variance < 0 ? "text-[#E65100]" : "text-[#1565C0]"}`}>{a.variance > 0 ? "+" : ""}{a.variance}</td>
                  <td className="px-4 py-2"><StatusBadge status={a.kind}/></td>
                </tr>
              ))}
              {abnormal.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-sm text-[#0D2B5E]/50">No abnormal variances in this period.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <Dialog title={editingId ? "Edit & Resubmit PO" : "New Purchase Order"} onClose={() => { setOpen(false); setEditingId(null); }} wide>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Supplier *</label>
              <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value, items: form.items.map(it => ({ ...it, material_id: "" })) })} data-testid="po-supplier" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">Select…</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <Input label="Expected Delivery" type="date" value={form.expected_delivery} onChange={(v) => setForm({ ...form, expected_delivery: v })}/>
            <Input label="Delivery Terms" value={form.delivery_terms} onChange={(v) => setForm({ ...form, delivery_terms: v })}/>
            <Input label="Payment Terms" value={form.payment_terms} onChange={(v) => setForm({ ...form, payment_terms: v })}/>
            <div className="col-span-2">
              <Input label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })}/>
            </div>
          </div>
          <div className="flex justify-between items-center mb-2">
            <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60">Items</div>
            <button onClick={addItem} className="text-xs text-[#1565C0] font-semibold uppercase tracking-wider" data-testid="po-add-item">+ Add item</button>
          </div>
          <table className="w-full text-sm border border-[#EEEEEE]">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-2 py-1">Material</th>
                <th className="text-left px-2 py-1">HSN</th>
                <th className="text-right px-2 py-1">Qty</th>
                <th className="text-center px-2 py-1">OPEN</th>
                <th className="text-right px-2 py-1">Rate</th>
                <th className="text-right px-2 py-1">GST %</th>
                <th className="text-right px-2 py-1">Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const qty = it.open_qty ? 0 : (+it.qty || 0);
                const lineTotal = qty * (+it.rate || 0);
                const lineGst = lineTotal * (+it.gst_slab || 0) / 100;
                return (
                <tr key={it._rowId || `row-${i}`}>
                  <td className="p-1">
                    <select value={it.material_id} onChange={(e) => onSelectMaterial(i, e.target.value)} disabled={!form.supplier_id} data-testid={`po-item-mat-${i}`} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm disabled:bg-[#F5F5F5]">
                      <option value="">Select…</option>
                      {filteredMaterials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
                    </select>
                  </td>
                  <td className="p-1">
                    <input type="text" value={it.hsn_code} placeholder={matHsn(it.material_id) || "—"} onChange={(e) => updateItem(i, { hsn_code: e.target.value })} data-testid={`po-item-hsn-${i}`} className="w-20 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono"/>
                  </td>
                  <td className="p-1"><input type="number" step="any" value={it.qty} disabled={!!it.open_qty} onChange={(e) => updateItem(i, { qty: e.target.value })} data-testid={`po-item-qty-${i}`} className="w-24 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right disabled:bg-[#F5F5F5] disabled:text-[#0D2B5E]/30"/></td>
                  <td className="p-1 text-center">
                    <label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" checked={!!it.open_qty} onChange={(e) => updateItem(i, { open_qty: e.target.checked })} data-testid={`po-item-open-${i}`}/> <span className={it.open_qty ? "text-[#E65100] font-bold uppercase tracking-widest" : "text-[#0D2B5E]/50"}>OPEN</span></label>
                  </td>
                  <td className="p-1"><input type="number" step="0.01" value={it.rate} onChange={(e) => updateItem(i, { rate: e.target.value })} data-testid={`po-item-rate-${i}`} className="w-24 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right"/></td>
                  <td className="p-1">
                    <select value={it.gst_slab} onChange={(e) => updateItem(i, { gst_slab: e.target.value })} data-testid={`po-item-gst-${i}`} className="w-20 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right">
                      {[0, 5, 12, 18, 28].map((s) => <option key={s} value={s}>{s}%</option>)}
                    </select>
                  </td>
                  <td className="p-1 text-right font-mono text-xs">
                    {it.open_qty ? <span className="text-[#E65100]">—</span> : (
                      <div className="flex flex-col items-end leading-tight">
                        <span>₹{lineTotal.toFixed(2)}</span>
                        <span className="text-[9px] text-[#0D2B5E]/50">+₹{lineGst.toFixed(2)} GST</span>
                      </div>
                    )}
                  </td>
                  <td className="p-1">{form.items.length > 1 && <button onClick={() => removeItem(i)} className="text-[#C62828] text-xs">Remove</button>}</td>
                </tr>
              );
              })}
            </tbody>
            <tfoot className="bg-[#F5F5F5] text-xs font-bold text-[#0D2B5E]">
              {(() => {
                const subtotal = form.items.reduce((a, it) => a + (it.open_qty ? 0 : (+it.qty || 0) * (+it.rate || 0)), 0);
                const gstTotal = form.items.reduce((a, it) => a + (it.open_qty ? 0 : (+it.qty || 0) * (+it.rate || 0) * (+it.gst_slab || 0) / 100), 0);
                return (
                  <>
                    <tr><td colSpan={6} className="px-2 py-1 text-right">Subtotal</td><td className="px-2 py-1 text-right font-mono">₹{subtotal.toFixed(2)}</td><td/></tr>
                    <tr><td colSpan={6} className="px-2 py-1 text-right">GST Total</td><td className="px-2 py-1 text-right font-mono">₹{gstTotal.toFixed(2)}</td><td/></tr>
                    <tr className="border-t border-[#0D2B5E]/30"><td colSpan={6} className="px-2 py-1 text-right uppercase tracking-widest">Grand Total</td><td className="px-2 py-1 text-right font-mono text-[#0D2B5E] text-sm">₹{(subtotal + gstTotal).toFixed(2)}</td><td/></tr>
                  </>
                );
              })()}
            </tfoot>
          </table>
          <div className="text-[10px] text-[#0D2B5E]/50 mt-2">OPEN PO — quantity is unlimited until manually pre-closed. Rate still applies to GRN price validation.</div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={save} disabled={submitBusy} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-po-btn">{submitBusy ? "Submitting…" : "Submit"}</button>
          </div>
        </Dialog>
      )}

      {view && (
        <Dialog title={`PO ${view.number}`} onClose={() => setView(null)} wide>
          {/* Feb-2026 (Wave E) — professional PO preview with supplier/consignee blocks, HSN/GST, and PDF download for approved POs. */}
          {["open", "partial", "closed", "pre_close_pending"].includes(view.status) && (
            <div className="flex justify-end mb-3">
              <a href={`${API_BASE}/purchase-orders/${view.id}/report.pdf`} target="_blank" rel="noreferrer"
                data-testid={`po-pdf-${view.number}`}
                className="text-xs bg-[#0D2B5E] text-white px-3 py-1.5 rounded-sm font-semibold tracking-wider uppercase inline-flex items-center gap-2 hover:bg-[#1565C0]">
                📄 Download PDF
              </a>
            </div>
          )}
          {view.status !== "pending_approval" && view.status !== "sent_back" && (
            <div className="mb-3 px-3 py-1.5 bg-[#2E7D32]/10 border-l-2 border-[#2E7D32] text-[10px] uppercase tracking-widest font-bold text-[#2E7D32] flex items-center gap-1">
              <Lock size={12}/> Read-only — approved POs cannot be edited
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="border border-[#EEEEEE] rounded-sm p-3 bg-[#F5F5F5]/50">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">Supplier (Bill To)</div>
              <div className="text-sm font-bold text-[#0D2B5E]">{supObj(view.supplier_id).name || "—"}</div>
              <div className="text-xs text-[#0D2B5E]/70 mt-1">{supObj(view.supplier_id).address || ""}</div>
              <div className="text-xs mt-1">GSTIN: <span className="font-mono">{supObj(view.supplier_id).gst_number || "—"}</span></div>
              <div className="text-xs">Contact: {supObj(view.supplier_id).contact_person || "—"} · {supObj(view.supplier_id).phone || "—"}</div>
              <div className="text-xs">Email: {supObj(view.supplier_id).email || "—"}</div>
            </div>
            <div className="border border-[#EEEEEE] rounded-sm p-3 bg-[#F5F5F5]/50">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">PO Meta</div>
              <div className="grid grid-cols-2 gap-x-3 text-xs">
                <div><span className="text-[#0D2B5E]/60">Number:</span> <span className="font-mono font-bold">{view.number}</span></div>
                <div><span className="text-[#0D2B5E]/60">Status:</span> <StatusBadge status={view.status}/></div>
                <div><span className="text-[#0D2B5E]/60">Created:</span> {view.created_by_name} · {view.created_at?.slice(0, 10)}</div>
                <div><span className="text-[#0D2B5E]/60">Approved:</span> {view.approved_by || "—"} {view.approved_at ? `· ${view.approved_at.slice(0, 10)}` : ""}</div>
                <div className="col-span-2"><span className="text-[#0D2B5E]/60">Expected Delivery:</span> {view.expected_delivery || "—"}</div>
                {view.delivery_terms && <div className="col-span-2"><span className="text-[#0D2B5E]/60">Delivery Terms:</span> {view.delivery_terms}</div>}
                {view.payment_terms && <div className="col-span-2"><span className="text-[#0D2B5E]/60">Payment Terms:</span> {view.payment_terms}</div>}
              </div>
            </div>
          </div>
          <table className="w-full text-sm border border-[#EEEEEE]">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-2 py-1">Material</th>
                <th className="text-left px-2 py-1">HSN</th>
                <th className="text-right px-2 py-1">Qty</th>
                <th className="text-right px-2 py-1">Rate</th>
                <th className="text-right px-2 py-1">GST %</th>
                <th className="text-right px-2 py-1">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(view.items || []).map((it, i) => {
                const amt = (it.qty || 0) * (it.rate || 0);
                return (
                <tr key={`${it.material_id || "row"}-${i}`} className={i % 2 === 0 ? "" : "bg-[#F5F5F5]/40"}>
                  <td className="px-2 py-1">{matCode(it.material_id)} — {matName(it.material_id)}</td>
                  <td className="px-2 py-1 font-mono text-xs">{it.hsn_code || matHsn(it.material_id) || "—"}</td>
                  <td className="px-2 py-1 font-mono text-right">{it.open_qty ? <span className="text-[#E65100] font-bold">OPEN</span> : it.qty}</td>
                  <td className="px-2 py-1 font-mono text-right">₹{(it.rate || 0).toFixed(2)}</td>
                  <td className="px-2 py-1 font-mono text-right">{it.gst_slab ?? 0}%</td>
                  <td className="px-2 py-1 font-mono text-right">{it.open_qty ? "—" : `₹${amt.toFixed(2)}`}</td>
                </tr>
              );})}
            </tbody>
            <tfoot className="bg-[#F5F5F5] text-xs font-bold text-[#0D2B5E]">
              {(() => {
                const subtotal = (view.items || []).reduce((a, it) => a + (it.open_qty ? 0 : (it.qty || 0) * (it.rate || 0)), 0);
                const gstTotal = (view.items || []).reduce((a, it) => a + (it.open_qty ? 0 : (it.qty || 0) * (it.rate || 0) * (it.gst_slab || 0) / 100), 0);
                return (
                  <>
                    <tr><td colSpan={5} className="px-2 py-1 text-right">Subtotal</td><td className="px-2 py-1 text-right font-mono">₹{subtotal.toFixed(2)}</td></tr>
                    <tr><td colSpan={5} className="px-2 py-1 text-right">GST Total</td><td className="px-2 py-1 text-right font-mono">₹{gstTotal.toFixed(2)}</td></tr>
                    <tr className="border-t border-[#0D2B5E]/30"><td colSpan={5} className="px-2 py-1 text-right uppercase tracking-widest">Grand Total</td><td className="px-2 py-1 text-right font-mono text-[#0D2B5E] text-sm">₹{(subtotal + gstTotal).toFixed(2)}</td></tr>
                  </>
                );
              })()}
            </tfoot>
          </table>
          {view.notes && <div className="text-xs mt-3"><span className="font-bold text-[#0D2B5E]/60">Notes:</span> {view.notes}</div>}
          {view.send_back_remark && <div className="text-xs mt-2 p-2 bg-[#E65100]/10 border-l-2 border-[#E65100]"><span className="font-bold text-[#E65100] uppercase tracking-widest text-[10px]">Sent Back:</span> {view.send_back_remark} <span className="text-[#0D2B5E]/50">— {view.sent_back_by}</span></div>}
          {view.pre_close_reason && <div className="text-xs mt-2 p-2 bg-[#0D2B5E]/5 border-l-2 border-[#0D2B5E]"><span className="font-bold text-[#0D2B5E] uppercase tracking-widest text-[10px]">Pre-Close Reason:</span> {view.pre_close_reason}</div>}
        </Dialog>
      )}

      {actionPo && (
        <Dialog title={`${actionPo.action === "review" ? "Review" : actionPo.action === "send-back" ? "Send Back" : actionPo.action === "reopen" ? "Reopen Request" : actionPo.action === "reopen-reject" ? "Reject Reopen" : "Pre-Close"} ${actionPo.po.number}`} onClose={() => setActionPo(null)}>
          {actionPo.action === "pre-close" && <div className="text-xs text-[#E65100] bg-[#E65100]/5 border border-[#E65100]/30 p-2 rounded-sm mb-3">Pre-close requires reason. SA approves directly; PH raises for SA confirmation.</div>}
          {actionPo.action === "review" && <div className="text-xs text-[#0D2B5E]/70 bg-[#1565C0]/5 border border-[#1565C0]/30 p-2 rounded-sm mb-3">Reject closes this PO permanently. Send Back returns it to the creator for edits.</div>}
          {actionPo.action === "reopen" && <div className="text-xs text-[#2E7D32] bg-[#2E7D32]/5 border border-[#2E7D32]/30 p-2 rounded-sm mb-3">Reopen reason is mandatory. SA reopens directly; PH/Admin or SA approves requests from other roles via bell-icon flow.</div>}
          {actionPo.action === "reopen-reject" && <div className="text-xs text-[#C62828] bg-[#C62828]/5 border border-[#C62828]/30 p-2 rounded-sm mb-3">PO stays closed. Rejection note is mandatory.</div>}
          <textarea placeholder={
            actionPo.action === "send-back" ? "Why is this being sent back?" :
            actionPo.action === "review" ? "Remark (required for Send Back / Reject)" :
            actionPo.action === "reopen" ? "Reason for reopening (e.g. revised production plan, short supply)" :
            actionPo.action === "reopen-reject" ? "Reason for rejecting the reopen" :
            "Reason for pre-close"
          } value={actionRemark} onChange={(e) => setActionRemark(e.target.value)} rows={3} data-testid="po-action-remark" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
          <div className="flex justify-end gap-2 mt-4">
            {actionPo.action === "review" ? (
              <>
                <button onClick={() => decidePO("reject")} className="px-4 py-2 bg-[#C62828] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="po-reject-btn">Reject</button>
                <button onClick={() => decidePO("send_back")} className="px-4 py-2 bg-[#E65100] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="po-sendback-btn">Send Back</button>
                <button onClick={() => decidePO("approve")} className="px-4 py-2 bg-[#2E7D32] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="po-approve-btn">Approve</button>
              </>
            ) : (
              <>
                <button onClick={() => setActionPo(null)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
                <button onClick={submitAction} className={`px-4 py-2 text-white text-sm rounded-sm font-semibold tracking-wider uppercase ${actionPo.action === "send-back" ? "bg-[#E65100]" : "bg-[#0D2B5E]"}`} data-testid="po-action-submit">{actionPo.action === "send-back" ? "Send Back" : "Raise Pre-Close"}</button>
              </>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="border-l-2 border-[#1565C0] pl-3">
      <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">{k}</div>
      <div className="text-sm font-medium text-[#0D2B5E]">{v}</div>
    </div>
  );
}
